import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import {
  COACH_DAILY_LIMIT,
  COACH_DISCLAIMER,
  COACH_MAX_QUESTION_CHARS,
  COACH_NAME,
  COACH_SUGGESTIONS,
  COACH_TAGLINE,
  exerciseLink,
  remainingQuota,
} from '@erectus/shared';
import type {
  AskCoachInput,
  AskCoachOutput,
  CoachMessage,
  CoachQuota,
  CoachTopic,
  Exercise,
} from '@erectus/shared';
import { Button, Card, Spinner } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { firebase } from '@/lib/firebase.js';
import { EXERCISE_LIBRARY } from '@/lib/repo.js';
import { peek, put, warm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { useAuth } from '@/stores/auth.js';
import { Icon } from '@/ui/icons.js';
import { CoachArchive, dayLabel } from './CoachArchive.js';
import { loadCoachDay, type CoachDay } from './coachThread.js';

/**
 * Darwin (docs/PRODUCT.md §11).
 *
 * Text-only Q&A over the user's own sessions, ten questions per local day, one
 * conversation per day (earlier days are an archive opened on request). The
 * answer is generated server-side: the context is read from Firestore there, so
 * a forged payload cannot make Darwin discuss anyone else's data, and the
 * wellness-scope filter runs on the output before it can reach this screen.
 */
export function CoachPage(): ReactNode {
  const { user } = useAuth();
  const threadKey = user ? warmKeys.coachThread(user.uid) : null;
  const quotaKey = user ? warmKeys.coachQuota(user.uid) : null;
  // Opened by the splash already, normally: start from what it fetched.
  const [messages, setMessages] = useState<CoachMessage[] | null>(() =>
    threadKey ? (peek<CoachMessage[]>(threadKey) ?? null) : null,
  );
  const exercises: readonly Exercise[] = EXERCISE_LIBRARY;
  const [quota, setQuota] = useState<CoachQuota | null>(() =>
    quotaKey ? (peek<CoachQuota>(quotaKey) ?? null) : null,
  );
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** An earlier day opened from the archive: read-only, replaces today's view. */
  const [viewing, setViewing] = useState<{ day: CoachDay; messages: CoachMessage[] | null } | null>(
    null,
  );

  const openDay = (day: CoachDay): void => {
    if (!user) return;
    setViewing({ day, messages: null });
    void loadCoachDay(user.uid, day.dayKey)
      .then((dayMessages) => setViewing({ day, messages: dayMessages }))
      .catch(() => setViewing({ day, messages: [] }));
  };
  const endRef = useRef<HTMLDivElement | null>(null);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (!user || !threadKey || !quotaKey) return;
    let cancelled = false;
    void warm(threadKey, () => loaders.coachThread(user.uid)).then((thread) => {
      if (!cancelled) setMessages((prev) => prev ?? thread ?? []);
    });
    // Not fatal when it fails: the ask itself reports the allowance back.
    void warm(quotaKey, () => loaders.coachQuota()).then((value) => {
      if (!cancelled && value) setQuota(value);
    });
    return () => {
      cancelled = true;
    };
  }, [user, threadKey, quotaKey]);

  // Keep the cache in step with what is on screen, so coming back to Darwin
  // never shows an older conversation than the one just left.
  useEffect(() => {
    if (threadKey && messages) put(threadKey, messages);
  }, [threadKey, messages]);
  useEffect(() => {
    if (quotaKey && quota) put(quotaKey, quota);
  }, [quotaKey, quota]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending]);

  const ask = useCallback(
    async (text: string, topic: CoachTopic) => {
      const trimmed = text.trim();
      if (!trimmed || pending) return;
      setPending(true);
      setError(null);
      setQuestion('');
      const localId = `local-${Date.now()}`;
      setMessages((prev) => [
        ...(prev ?? []),
        { id: localId, role: 'user', text: trimmed, createdAt: Date.now() },
      ]);

      try {
        const call = httpsCallable<AskCoachInput, AskCoachOutput>(
          firebase().functions,
          'askCoach',
        );
        const res = await call({ question: trimmed, topic, tz });
        setMessages((prev) => [
          ...(prev ?? []),
          {
            id: `coach-${Date.now()}`,
            role: 'coach',
            text: res.data.answer,
            exerciseIds: res.data.exerciseIds,
            safetyFlags: res.data.safetyFlags,
            createdAt: Date.now(),
          },
        ]);
        setQuota(res.data.quota);
      } catch (callError) {
        const message = (callError as { message?: string }).message ?? '';
        const code = (callError as { code?: string }).code ?? '';
        setError(
          code.includes('resource-exhausted')
            ? message ||
              `That is all ${COACH_DAILY_LIMIT} questions for today. They reset at midnight.`
            : code.includes('failed-precondition')
              ? 'Darwin is not configured for this project yet.'
              : 'Darwin could not answer that. Try again in a moment.',
        );
        // Drop the optimistic question: it was never charged or answered.
        setMessages((prev) => (prev ?? []).filter((m) => m.id !== localId));
      } finally {
        setPending(false);
      }
    },
    [pending, tz],
  );

  const left = quota ? remainingQuota(quota) : null;
  const exhausted = left === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={COACH_NAME}
        subtitle={COACH_TAGLINE}
        action={
          left === null ? null : (
            <span className="shrink-0 rounded-full bg-surface px-2.5 py-1 text-xs font-semibold text-muted">
              {left}/{COACH_DAILY_LIMIT} left
            </span>
          )
        }
      />

      {viewing ? (
        <div className="space-y-3">
          <Card className="flex items-center justify-between gap-3 bg-surface">
            <span className="text-sm">
              <span className="font-semibold">{dayLabel(viewing.day.dayKey)}</span>
              <span className="text-muted"> · read only</span>
            </span>
            <Button variant="secondary" onClick={() => setViewing(null)}>
              Back to today
            </Button>
          </Card>
          {viewing.messages === null ? (
            <Card className="grid place-items-center py-8">
              <Spinner label="Opening the conversation" />
            </Card>
          ) : (
            viewing.messages.map((message) => (
              <Message key={message.id} message={message} exercises={exercises} />
            ))
          )}
        </div>
      ) : messages === null ? (
        <Card className="grid place-items-center py-8">
          <Spinner label="Opening the conversation" />
        </Card>
      ) : messages.length === 0 ? (
        <Card className="space-y-2">
          <p className="text-sm font-semibold">A fresh conversation for today.</p>
          <p className="text-sm text-muted">
            Darwin can see your recorded sessions — scores, neck angle, how long before your
            posture first slipped — and the exercise library. It cannot see anything else about
            you, and it does not ask.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <Message key={message.id} message={message} exercises={exercises} />
          ))}
        </div>
      )}

      {pending ? (
        <Card className="flex items-center gap-2">
          <Spinner label={`${COACH_NAME} is reading your sessions`} />
        </Card>
      ) : null}

      {error ? (
        <Card className="border-[color:var(--color-state-slouch)] bg-[color:var(--color-state-slouch-soft)]">
          <p className="text-sm">{error}</p>
        </Card>
      ) : null}

      <div ref={endRef} />

      {viewing ? null : !exhausted ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {COACH_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion.topic}
                disabled={pending}
                onClick={() => void ask(suggestion.question, suggestion.topic)}
                className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink-soft disabled:opacity-50"
              >
                {suggestion.label}
              </button>
            ))}
          </div>

          <Card className="space-y-2">
            <label className="block">
              <span className="sr-only">Your question</span>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value.slice(0, COACH_MAX_QUESTION_CHARS))}
                rows={3}
                placeholder="Ask anything about your posture sessions…"
                className="w-full resize-none rounded-xl border border-line px-3 py-2.5 text-sm outline-none focus:border-ink"
              />
            </label>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted">
                {question.length}/{COACH_MAX_QUESTION_CHARS}
              </span>
              <Button
                disabled={pending || question.trim().length < 3}
                onClick={() => void ask(question, 'free')}
              >
                Ask {COACH_NAME}
              </Button>
            </div>
          </Card>
        </div>
      ) : (
        <Card>
          <p className="text-sm font-semibold">That is today&apos;s {COACH_DAILY_LIMIT} questions.</p>
          <p className="mt-1 text-sm text-muted">
            The allowance resets at midnight{quota ? `, ${formatReset(quota.resetsAt)}` : ''}.
          </p>
        </Card>
      )}

      {user ? <CoachArchive uid={user.uid} onOpen={openDay} /> : null}

      <p className="text-xs leading-relaxed text-muted">{COACH_DISCLAIMER}</p>
    </div>
  );
}

function Message({
  message,
  exercises,
}: {
  message: CoachMessage;
  exercises: readonly Exercise[];
}): ReactNode {
  const mine = message.role === 'user';
  const picked = (message.exerciseIds ?? [])
    .map((id) => exercises.find((e) => e.id === id))
    .filter((e): e is Exercise => Boolean(e));

  return (
    <div className={mine ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          mine ? 'bg-ink text-white' : 'border border-line bg-card text-ink'
        }`}
      >
        {!mine ? (
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
            {COACH_NAME}
          </p>
        ) : null}
        <p className="selectable whitespace-pre-wrap">{message.text}</p>
        {picked.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {picked.map((exercise) => (
              <li key={exercise.id}>
                {/* Straight to that exercise, already open. */}
                <Link
                  to={exerciseLink(exercise.id)} state={{ from: 'coach' }}
                  className="flex items-center justify-between gap-2 rounded-lg bg-surface px-2.5 py-2 text-xs font-medium text-ink"
                >
                  <span>
                    {exercise.name} · {exercise.reps}
                  </span>
                  <Icon name="back" size={16} className="rotate-180 text-muted" />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function formatReset(at: number): string {
  const hours = Math.max(0, Math.round((at - Date.now()) / (60 * 60 * 1000)));
  return hours <= 1 ? 'within the hour' : `in about ${hours} hours`;
}
